import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Moon, Droplets, Dumbbell, Flame, Smile, X, Sparkles, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const METRIC_ICONS = {
    sleep_hours: { Icon: Moon, color: '#6366f1' },
    water_ml: { Icon: Droplets, color: '#3b82f6' },
    workouts_per_day: { Icon: Dumbbell, color: '#22c55e' },
    calories: { Icon: Flame, color: '#f97316' },
    mood_score: { Icon: Smile, color: '#ec4899' },
};

const dismissKey = 'symptom_prediction_dismissed';

function getDismissed() {
    try {
        const raw = localStorage.getItem(dismissKey);
        return raw ? JSON.parse(raw) : { date: null, signatures: [] };
    } catch { return { date: null, signatures: [] }; }
}

function setDismissed(signatures) {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(dismissKey, JSON.stringify({ date: today, signatures }));
}

export default function SymptomPredictionBanner() {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [warnings, setWarnings] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API}/api/health/symptoms/prediction?days=90`);
                const fresh = res.data?.warnings || [];
                // Filter out dismissed (same day, same signature)
                const today = new Date().toISOString().slice(0, 10);
                const d = getDismissed();
                const dismissedToday = d.date === today ? new Set(d.signatures) : new Set();
                const filtered = fresh.filter(w => !dismissedToday.has(`${w.symptom}:${w.metric}`));
                setWarnings(filtered);
            } catch (e) { console.error(e); }
            finally { setLoaded(true); }
        };
        load();
    }, []);

    const t = (de, en) => (language === 'de' ? de : en);

    const handleAction = (w) => {
        const a = w.suggested_action?.action;
        if (!a) return;
        switch (a) {
            case 'sleep_reminder': navigate('/settings?focus=notifications'); break;
            case 'log_water':
            case 'review_nutrition': navigate('/nutrition'); break;
            case 'log_mood': navigate('/dashboard'); break;
            case 'log_workout': navigate('/fitness'); break;
            default: break;
        }
    };

    const dismiss = (w) => {
        const sig = `${w.symptom}:${w.metric}`;
        const d = getDismissed();
        const today = new Date().toISOString().slice(0, 10);
        const sigs = d.date === today ? Array.from(new Set([...(d.signatures || []), sig])) : [sig];
        setDismissed(sigs);
        setWarnings(prev => prev.filter(x => `${x.symptom}:${x.metric}` !== sig));
    };

    if (!loaded || warnings.length === 0) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-rose-500/10 p-3 sm:p-4 space-y-2"
                data-testid="symptom-prediction-banner"
            >
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                    </div>
                    <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-500 uppercase tracking-wider">
                            {t("🐺 Nanouk's Vorhersage für heute", "🐺 Nanouk's prediction for today")}
                        </p>
                    </div>
                </div>
                <div className="space-y-2">
                    {warnings.map((w, idx) => {
                        const { Icon, color } = METRIC_ICONS[w.metric] || { Icon: AlertTriangle, color: '#f59e0b' };
                        const valueStr = `${w.yesterday_value}${w.unit}`;
                        const onSymStr = `${w.on_symptom_avg}${w.unit}`;
                        const baselineStr = `${w.on_clean_avg}${w.unit}`;
                        const metricLabel = language === 'de' ? w.metric_label_de : w.metric_label_en;
                        const directionText = w.direction === 'less'
                            ? t(`weniger ${metricLabel}`, `less ${metricLabel.toLowerCase()}`)
                            : t(`mehr ${metricLabel}`, `more ${metricLabel.toLowerCase()}`);
                        const sa = w.suggested_action;
                        const actionLabel = sa ? (language === 'de' ? sa.label_de : sa.label_en) : null;
                        return (
                            <div
                                key={`${w.symptom}-${w.metric}`}
                                className="flex items-start gap-2 p-2.5 rounded-xl bg-card/80 border border-border"
                                data-testid={`prediction-warning-${idx}`}
                            >
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}20` }}>
                                    <Icon className="w-4 h-4" style={{ color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold leading-tight">
                                        {t(`Erhöhtes ${w.symptom}-Risiko`, `Elevated ${w.symptom} risk`)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                                        {t(
                                            `Gestern: ${metricLabel} ${valueStr} — historisch: vor Symptom-Tagen Ø ${onSymStr} vs. clean ${baselineStr}.`,
                                            `Yesterday: ${metricLabel} ${valueStr} — historically before symptom days Ø ${onSymStr} vs. clean ${baselineStr}.`
                                        )}
                                    </p>
                                    <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium mt-1 inline-flex items-center gap-1">
                                        <TrendingUp className="w-3 h-3" />
                                        {t(
                                            `${w.conditional_pct}% Risiko (${w.lift}× über Baseline ${w.baseline_pct}%)`,
                                            `${w.conditional_pct}% risk (${w.lift}× over baseline ${w.baseline_pct}%)`
                                        )}
                                    </p>
                                    {actionLabel && (
                                        <button
                                            type="button"
                                            onClick={() => handleAction(w)}
                                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                                            data-testid={`prediction-action-${idx}`}
                                        >
                                            <Sparkles className="w-3 h-3" />
                                            {actionLabel}
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => dismiss(w)}
                                    className="text-muted-foreground hover:text-foreground shrink-0"
                                    aria-label="dismiss"
                                    data-testid={`prediction-dismiss-${idx}`}
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[10px] text-muted-foreground/70 italic pt-1">
                    {t(
                        'Basierend auf deinen letzten 90 Tagen — keine Diagnose, nur ein Hinweis.',
                        'Based on your last 90 days — not a diagnosis, just a hint.'
                    )}
                </p>
            </motion.div>
        </AnimatePresence>
    );
}
